import type {ReactNode} from 'react'
import {useRef} from 'react'
import {useSafeEffect} from '@freckle/react-hooks'

type MathJaxT = {
  startup: {
    promise: Promise<unknown>
  }
  typesetPromise: (nodes?: NodeListOf<HTMLElement> | HTMLElement[]) => Promise<unknown>
}
type WindowWithMathjax = Window & {
  mathjaxLoadedP: Promise<unknown>
}
declare let MathJax: MathJaxT

type Props = {
  children: ReactNode
  inline?: boolean
}

export const EnableMathJax = (props: Props) => {
  const {children, inline = false} = props
  const observer = useRef<MutationObserver | null>(null)
  const divRef = useRef<HTMLDivElement>(null)
  const spanRef = useRef<HTMLSpanElement>(null)

  useSafeEffect(() => {
    const container = inline ? spanRef.current : divRef.current
    if (!container) return

    // Initial typeset for this container
    typeset(container)

    // Set up observer for this specific container
    observer.current = new MutationObserver(() => {
      typeset(container)
    })

    observer.current.observe(container, {
      childList: true,
      attributes: true,
      characterData: true,
      subtree: true,
    })

    return () => {
      if (observer.current !== null) {
        observer.current.disconnect()
      }
    }
  }, [inline])

  if (inline) {
    return (
      <span ref={spanRef} data-mathjax>
        {children}
      </span>
    )
  } else {
    return (
      <div ref={divRef} data-mathjax>
        {children}
      </div>
    )
  }
}

const typeset = (node: HTMLElement): Promise<unknown> => {
  // https://docs.mathjax.org/en/latest/web/typeset.html#handling-asynchronous-typesetting
  return (window as unknown as WindowWithMathjax).mathjaxLoadedP.then(() => {
    MathJax.startup.promise = MathJax.startup.promise
      .then(() => MathJax.typesetPromise([node]))
      .catch((_err: Error) => {})
    return MathJax.startup.promise
  })
}
